import React, { useState, useEffect } from "react";
import Pagination from "../common/Pagination";
import NavBar from "../common/NavBar";
import Footer from "../common/Footer";
import OwnerMenu from "../owner/OwnerMenu";
import CreateCurrencyModal from "../owner/OwnerModals/CreateCurrencyModal"; // Import the modal component
import { useTranslation } from "react-i18next";

const Currencies = () => {
  const { t } = useTranslation();
  const [currencies, setCurrencies] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currencyToUpdate, setCurrencyToUpdate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 5;

  // Determine the API base URL based on the environment
  const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_URL || "http://localhost:8000";
  };

  // Fetch all currencies
  useEffect(() => {
    fetch(`${getApiBaseUrl()}/api/currencies/`, {
      headers: { "Accept": "application/json" },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then((data) => setCurrencies(data))
      .catch((error) => console.error("Error fetching currencies:", error));
  }, []);

  // Handle delete currency
  const handleDelete = async (currencyId) => {
    if (!window.confirm(t("are_you_sure_delete_currency"))) return;

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/currencies/${currencyId}/`, {
        method: "DELETE",
      });

      if (response.ok) {
        setCurrencies((prev) => prev.filter((cur) => cur.id !== currencyId));
        alert(t("Currency deleted successfully"));
      } else {
        alert(t("Error deleting currency"));
      }
    } catch (error) {
      console.error("Error deleting currency:", error);
      alert(t("Error occurred try again"));
    }
  };

  // Open the modal for creating a new currency
  const openCreateModal = () => {
    setCurrencyToUpdate(null);
    setIsModalOpen(true);
  };

  // Open the modal for updating an existing currency
  const openUpdateModal = (currency) => {
    setCurrencyToUpdate(currency);
    setIsModalOpen(true);
  };

  // Handle modal close
  const closeModal = () => {
    setIsModalOpen(false);
    setCurrencyToUpdate(null);
  };

  // Handle modal save (create or update)
  const handleSave = async (currencyData) => {
    try {
      const method = currencyToUpdate ? "PUT" : "POST";
      const url = currencyToUpdate
        ? `${getApiBaseUrl()}/api/currencies/${currencyToUpdate.id}/`
        : `${getApiBaseUrl()}/api/currencies/`;

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(currencyData),
      });

      if (response.ok) {
        const updatedCurrency = await response.json();

        if (currencyToUpdate) {
          // Update the existing currency in the list
          setCurrencies((prev) =>
            prev.map((cur) => (cur.id === updatedCurrency.id ? updatedCurrency : cur))
          );
        } else {
          // Add the new currency to the list
          setCurrencies((prev) => [...prev, updatedCurrency]);
        }

        alert(t("Currency saved successfully"));
        closeModal();
      } else {
        alert(t("Error saving currency"));
      }
    } catch (error) {
      console.error("Error saving currency:", error);
      alert(t("Error occurred try again"));
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <OwnerMenu />
      <div className="flex flex-col items-center mt-16">
        <h1 className="text-3xl font-bold mb-8">{t("Currencies")}</h1>

        <button
          onClick={openCreateModal}
          className="bg-blue-500 text-white px-4 py-2 rounded-md mb-4 hover:bg-blue-700"
        >
          {t("Create Currency")}
        </button>

        <div className="w-full max-w-4xl overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-300">
            <thead>
              <tr className="bg-gray-100 text-center">
                <th className="py-3 px-4 border-b">{t("name")}</th>
                <th className="py-3 px-4 border-b">{t("Symbol")}</th>
                <th className="py-3 px-4 border-b">{t("Code")}</th>
                <th className="py-3 px-4 border-b">{t("Exchange Rate to USD")}</th>
                <th className="py-3 px-4 border-b">{t("default")}</th>
                <th className="py-3 px-4 border-b">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {currencies.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((currency) => (
                <tr key={currency.id} className="hover:bg-gray-50 text-center">
                  <td className="py-3 px-4 border-b">{currency.name}</td>
                  <td className="py-3 px-4 border-b">{currency.symbol}</td>
                  <td className="py-3 px-4 border-b">{currency.code}</td>
                  <td className="py-3 px-4 border-b">{currency.exchange_rate_to_usd}</td>
                  <td className="py-3 px-4 border-b">
                    {currency.is_default ? (
                      <span className="text-green-600 font-bold">✓</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-3 px-4 border-b flex justify-center space-x-2">
                    <button
                      onClick={() => openUpdateModal(currency)}
                      className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-700"
                    >
                      {t("Update")}
                    </button>
                    <button
                      onClick={() => handleDelete(currency.id)}
                      className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-700"
                    >
                      {t("Delete")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination totalItems={currencies.length} pageSize={PAGE_SIZE} currentPage={currentPage} onPageChange={setCurrentPage} />
      </div>

      {/* Create/Update Modal */}
      {isModalOpen && (
        <CreateCurrencyModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSave={handleSave}
          currency={currencyToUpdate}
        />
      )}

      <Footer />
    </div>
  );
};

export default Currencies;